import { BASE_URL, resolveApiPath } from '../baseUrl';
import { AssetRequestError, requestAssetApi } from './getAssetById';
import type { SocialPostMediaKind, SocialPostRecord, SocialPostUserGroup } from '../utils/socialPostsStorage';

/** Match stories API: `userAvatarSrc` max length 2048. */
const MAX_STORED_AVATAR_CHARS = 2048;

function isStoryMediaPath(src: string): boolean {
  return /\/stories\/[^/?#]+\/[^/?#]+/i.test(src) && !/\/profiles\//i.test(src);
}

function sanitizeSocialPostAvatarSrc(avatarSrc: string | null | undefined): string | null {
  if (typeof avatarSrc !== 'string') {
    return null;
  }
  const trimmed = avatarSrc.trim();
  if (!trimmed || isStoryMediaPath(trimmed)) {
    return null;
  }
  if (trimmed.length > MAX_STORED_AVATAR_CHARS) {
    return null;
  }
  return trimmed;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function pickString(...values: unknown[]): string | null {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return null;
}

function parseTimestampMs(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value < 1e12 ? Math.round(value * 1000) : Math.round(value);
  }
  if (typeof value === 'string' && value.trim()) {
    const ms = Date.parse(value.trim());
    if (Number.isFinite(ms)) {
      return ms;
    }
  }
  return null;
}

function pickAvatarSrc(...values: unknown[]): string | null {
  return sanitizeSocialPostAvatarSrc(pickString(...values));
}

export function normalizeStoryUserId(userId: string): string {
  return userId.trim().toLowerCase();
}

function storyPostHasPlayableMedia(post: SocialPostRecord): boolean {
  if (post.mediaKind === 'video') {
    return Boolean(post.videoUrl?.trim());
  }
  return Boolean(post.imageUrl?.trim());
}

function enrichStoryPostWireRow(
  post: unknown,
  groupDefaults: { userId: string; userLabel: string; userAvatarSrc: string | null },
): unknown {
  if (!isRecord(post)) {
    return post;
  }
  return {
    ...post,
    userId: pickString(post.userId, post.user_id) ?? groupDefaults.userId,
    userLabel:
      pickString(post.userLabel, post.user_label, post.userName, post.user_name) ??
      groupDefaults.userLabel,
    userAvatarSrc:
      pickAvatarSrc(
        post.userAvatarSrc,
        post.user_avatar_src,
        post.userAvatarUrl,
        post.user_avatar_url,
        post.avatarUrl,
        post.avatar_url,
      ) ?? groupDefaults.userAvatarSrc,
  };
}

function resolveMediaKind(row: Record<string, unknown>): SocialPostMediaKind {
  const explicit = pickString(row.mediaKind, row.media_kind, row.type, row.storyType);
  if (explicit === 'video') {
    return 'video';
  }
  if (explicit === 'imageAudio' || explicit === 'image_audio' || explicit === 'image') {
    return 'imageAudio';
  }
  const videoUrl = pickString(row.videoUrl, row.video_url);
  if (videoUrl) {
    return 'video';
  }
  return 'imageAudio';
}

/** Join API-relative media paths for `<img>`, `<video>`, and `<audio>` sources. */
export function resolveStoryMediaSrc(raw: string | null | undefined): string | null {
  if (!raw?.trim()) {
    return null;
  }
  const s = raw.trim();
  if (/^https?:\/\//i.test(s) || s.startsWith('data:') || s.startsWith('blob:')) {
    return s;
  }
  const base = BASE_URL.replace(/\/+$/, '');
  // Already resolved for this app origin (avoid /dev-api-proxy/dev-api-proxy/…).
  if (base && (s === base || s.startsWith(`${base}/`))) {
    return s;
  }
  if (base.startsWith('/')) {
    if (s.startsWith('/images/')) {
      return s;
    }
    return `${base}${s.startsWith('/') ? s : `/${s}`}`;
  }
  return `${base}${s.startsWith('/') ? s : `/${s}`}`;
}

export function parseSocialStoryRecord(row: unknown): SocialPostRecord | null {
  if (!isRecord(row)) {
    return null;
  }
  const id = pickString(row.id, row.storyId, row.story_id);
  const userIdRaw = pickString(row.userId, row.user_id, row.ownerId, row.owner_id);
  if (!id || !userIdRaw) {
    return null;
  }
  const userId = normalizeStoryUserId(userIdRaw);
  const createdAt = parseTimestampMs(row.createdAt ?? row.created_at) ?? Date.now();
  const expiresAt =
    parseTimestampMs(row.expiresAt ?? row.expires_at ?? row.expireAt ?? row.expire_at) ??
    createdAt + 48 * 60 * 60 * 1000;
  const imageUrl = resolveStoryMediaSrc(pickString(row.imageUrl, row.image_url));
  const audioUrl = resolveStoryMediaSrc(pickString(row.audioUrl, row.audio_url));
  const videoUrl = resolveStoryMediaSrc(pickString(row.videoUrl, row.video_url));
  const mediaKind = resolveMediaKind(row);
  let userAvatarSrc = sanitizeSocialPostAvatarSrc(
    pickString(
      row.userAvatarSrc,
      row.user_avatar_src,
      row.userAvatarUrl,
      row.user_avatar_url,
      row.avatarUrl,
      row.avatar_url,
    ),
  );
  // Never treat the story media itself as the profile chip.
  if (
    userAvatarSrc &&
    ((imageUrl && userAvatarSrc === imageUrl) || (videoUrl && userAvatarSrc === videoUrl))
  ) {
    userAvatarSrc = null;
  }
  return {
    id,
    userId,
    userLabel:
      pickString(row.userLabel, row.user_label, row.userName, row.user_name, row.displayName) ??
      userIdRaw,
    userAvatarSrc,
    text: pickString(row.text, row.message, row.caption) ?? '',
    mediaKind,
    imageUrl: imageUrl ?? undefined,
    audioUrl: audioUrl ?? undefined,
    videoUrl: videoUrl ?? undefined,
    createdAt,
    expiresAt,
  };
}

function extractStoryRows(raw: unknown): unknown[] {
  if (Array.isArray(raw)) {
    return raw;
  }
  if (!isRecord(raw)) {
    return [];
  }
  for (const key of ['data', 'stories', 'items', 'result', 'posts']) {
    const value = raw[key];
    if (Array.isArray(value)) {
      return value;
    }
  }
  return [];
}

function flattenStoryWireRows(raw: unknown): unknown[] {
  const rows = Array.isArray(raw) ? raw : extractStoryRows(raw);
  const flat: unknown[] = [];
  for (const item of rows) {
    if (isRecord(item) && Array.isArray(item.posts)) {
      const userIdRaw = pickString(item.userId, item.user_id) ?? '';
      const groupDefaults = {
        userId: userIdRaw,
        userLabel:
          pickString(item.userLabel, item.user_label, item.userName, item.user_name) ?? userIdRaw,
        userAvatarSrc: pickAvatarSrc(
          item.userAvatarSrc,
          item.user_avatar_src,
          item.userAvatarUrl,
          item.user_avatar_url,
          item.avatarUrl,
          item.avatar_url,
        ),
      };
      for (const post of item.posts) {
        flat.push(enrichStoryPostWireRow(post, groupDefaults));
      }
      continue;
    }
    flat.push(item);
  }
  return flat;
}

/** GET `/stories/` returns `{ userId, userAvatarSrc, posts[] }[]` — preserve group avatars. */
export function parseSocialStoriesAsUserGroups(raw: unknown): SocialPostUserGroup[] | null {
  const rows = Array.isArray(raw) ? raw : extractStoryRows(raw);
  if (!rows.some((item) => isRecord(item) && Array.isArray(item.posts))) {
    return null;
  }
  const groups: SocialPostUserGroup[] = [];
  for (const item of rows) {
    if (!isRecord(item) || !Array.isArray(item.posts)) {
      continue;
    }
    const userIdRaw = pickString(item.userId, item.user_id);
    if (!userIdRaw) {
      continue;
    }
    const groupAvatar = pickAvatarSrc(
      item.userAvatarSrc,
      item.user_avatar_src,
      item.userAvatarUrl,
      item.user_avatar_url,
      item.avatarUrl,
      item.avatar_url,
    );
    const groupLabel =
      pickString(item.userLabel, item.user_label, item.userName, item.user_name) ?? userIdRaw;
    const groupDefaults = {
      userId: userIdRaw,
      userLabel: groupLabel,
      userAvatarSrc: groupAvatar,
    };
    const posts: SocialPostRecord[] = [];
    for (const post of item.posts) {
      const parsed = parseSocialStoryRecord(enrichStoryPostWireRow(post, groupDefaults));
      if (parsed && storyPostHasPlayableMedia(parsed)) {
        posts.push(parsed);
      }
    }
    if (posts.length === 0) {
      continue;
    }
    posts.sort((a, b) => a.createdAt - b.createdAt);
    let resolvedAvatar: string | null = null;
    for (let i = posts.length - 1; i >= 0; i -= 1) {
      const candidate = posts[i]?.userAvatarSrc?.trim();
      if (candidate) {
        resolvedAvatar = candidate;
        break;
      }
    }
    if (!resolvedAvatar && groupAvatar) {
      resolvedAvatar = groupAvatar;
    }
    groups.push({
      userId: normalizeStoryUserId(userIdRaw),
      userLabel: groupLabel,
      userAvatarSrc: resolvedAvatar,
      posts,
    });
  }
  if (groups.length === 0) {
    return null;
  }
  groups.sort((a, b) => {
    const aLatest = a.posts[a.posts.length - 1]?.createdAt ?? 0;
    const bLatest = b.posts[b.posts.length - 1]?.createdAt ?? 0;
    return bLatest - aLatest;
  });
  return groups;
}

export function parseSocialStoriesList(raw: unknown): SocialPostRecord[] {
  const rows = flattenStoryWireRows(raw);
  const out: SocialPostRecord[] = [];
  for (const row of rows) {
    const parsed = parseSocialStoryRecord(row);
    if (parsed) {
      out.push(parsed);
    }
  }
  return out;
}

export type CreateSocialStoryInput = {
  text: string;
  mediaKind: SocialPostMediaKind;
  userLabel: string;
  userAvatarSrc: string | null;
  imageFile?: Blob;
  audioFile?: Blob;
  videoFile?: Blob;
};

/** Wire enum for POST `/stories/` — matches API validation (`imageAudio` | `video`). */
export function wireStoryMediaKind(mediaKind: SocialPostMediaKind): SocialPostMediaKind {
  return mediaKind === 'video' ? 'video' : 'imageAudio';
}

function asStoryUploadFile(blob: Blob, filename: string, mimeType: string): File {
  if (blob instanceof File) {
    if (blob.type) {
      return blob;
    }
    return new File([blob], blob.name || filename, { type: mimeType });
  }
  return new File([blob], filename, { type: blob.type || mimeType });
}

function blobFileName(blob: Blob, fallback: string): string {
  if (blob instanceof File && blob.name.trim()) {
    return blob.name;
  }
  return fallback;
}

export function buildCreateSocialStoryFormData(input: CreateSocialStoryInput): FormData {
  const fd = new FormData();
  const trimmedText = input.text.trim();
  if (trimmedText) {
    fd.append('text', trimmedText);
  }
  fd.append('mediaKind', wireStoryMediaKind(input.mediaKind));
  fd.append('userLabel', input.userLabel.trim().slice(0, 255));
  const avatar = sanitizeSocialPostAvatarSrc(input.userAvatarSrc);
  if (avatar) {
    fd.append('userAvatarSrc', avatar);
  }
  if (input.imageFile) {
    const name = blobFileName(input.imageFile, 'image.jpg');
    fd.append(
      'image',
      asStoryUploadFile(input.imageFile, name, 'image/jpeg'),
      name,
    );
  }
  if (input.audioFile) {
    const name = blobFileName(input.audioFile, 'audio.webm');
    fd.append(
      'audio',
      asStoryUploadFile(input.audioFile, name, input.audioFile.type || 'audio/webm'),
      name,
    );
  }
  if (input.videoFile) {
    const name = blobFileName(input.videoFile, 'video.webm');
    fd.append(
      'video',
      asStoryUploadFile(input.videoFile, name, input.videoFile.type || 'video/webm'),
      name,
    );
  }
  return fd;
}

function mapCreateStoryError(err: unknown): Error {
  if (err instanceof AssetRequestError) {
    const haystack = `${err.message} ${err.rawBody ?? ''} ${err.errorDescription ?? ''}`.toLowerCase();
    if (err.httpStatus === 500) {
      return new Error('social-server-error');
    }
    if (err.httpStatus === 413 || haystack.includes('too large') || haystack.includes('payload too large')) {
      if (haystack.includes('video')) {
        return new Error('social-video-too-large');
      }
      if (haystack.includes('audio')) {
        return new Error('social-audio-too-large');
      }
      if (haystack.includes('image') || haystack.includes('photo')) {
        return new Error('social-image-too-large');
      }
      return new Error('social-video-too-large');
    }
    if (
      haystack.includes('limit') ||
      haystack.includes('maximum') ||
      haystack.includes('max active') ||
      haystack.includes('too many')
    ) {
      const limitErr = new Error('social-post-limit');
      limitErr.name = 'SocialPostLimitError';
      return limitErr;
    }
    if (err.httpStatus === 400 && (haystack.includes('media') || haystack.includes('image') || haystack.includes('video'))) {
      return new Error('social-missing-media');
    }
  }
  return new Error('social-save-failed');
}

/** GET `/stories/` — raw wire JSON. */
export async function fetchSocialStoriesWire(): Promise<unknown> {
  const url = resolveApiPath('stories/');
  console.info('[socialPosts] GET stories', url);
  return requestAssetApi({ method: 'GET', path: '/stories/' });
}

/** GET `/stories/` — active stories for the home strip. */
export async function fetchActiveSocialStories(): Promise<SocialPostRecord[]> {
  try {
    const raw = await fetchSocialStoriesWire();
    return parseSocialStoriesList(raw);
  } catch {
    return [];
  }
}

/** POST `/stories/` — multipart upload. */
export async function createSocialStory(input: CreateSocialStoryInput): Promise<SocialPostRecord> {
  try {
    const raw = await requestAssetApi({
      method: 'POST',
      path: '/stories/',
      body: buildCreateSocialStoryFormData(input),
    });
    const parsed = isRecord(raw) ? parseSocialStoryRecord(raw) : null;
    if (parsed) {
      return parsed;
    }
    const nested = isRecord(raw) ? parseSocialStoryRecord(raw.data ?? raw.story) : null;
    if (nested) {
      return nested;
    }
    throw new Error('social-save-failed');
  } catch (err) {
    if (err instanceof Error && err.message.startsWith('social-')) {
      throw err;
    }
    throw mapCreateStoryError(err);
  }
}

/** DELETE `/stories/:id`. */
export async function deleteSocialStory(storyId: string): Promise<void> {
  const id = storyId.trim();
  if (!id) {
    return;
  }
  await requestAssetApi({
    method: 'DELETE',
    path: `/stories/${encodeURIComponent(id)}`,
  });
}

/** Resolved browser URL for a stories route (for debugging). */
export function storiesApiUrl(pathTail = ''): string {
  const tail = pathTail.replace(/^\/+/, '');
  if (!tail) {
    return resolveApiPath('stories/');
  }
  return resolveApiPath(`stories/${tail}`);
}
